import { type PropsWithChildren, useCallback, useEffect } from 'react';
import {
  type LayoutChangeEvent,
  Platform,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import Animated, {
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withDelay,
  withSpring,
  withTiming,
} from 'react-native-reanimated';

import { theme } from '@garnish/constants';
import debounce from 'lodash.debounce';

import { useUniqueNativeID } from '../../../../../../hooks';
import {
  DIALOG_ANIMATIONS,
  DIALOG_HEIGHT_EXPANDED,
  DIALOG_PADDING,
  DIALOG_SIZE_COLLAPSED,
  TOGGLE_SIZE,
} from '../../Floating.contants';
import { Toggle } from '../Toggle';

// ─── Components ──────────────────────────────────────────────────────────────

export const Dialog = (props: Props) => {
  const { isShown, children, toggleA11yLabel, dialogA11yLabel, onTogglePress } = props;

  const { width: viewportWidth } = useWindowDimensions();
  const dialogNativeId = useUniqueNativeID('nutrition-calculator-dialog');

  // ─── Flags ───────────────────────────────────────────────────────────

  const isSmallScreen = viewportWidth <= 460;

  // ─── Shared Values ───────────────────────────────────────────────────

  const isShownSV = useSharedValue(0);
  const isContentShownSV = useSharedValue(0);
  const contentWidthSV = useSharedValue(0);

  const dialogWidthSV = useDerivedValue(
    () => contentWidthSV.value + DIALOG_SIZE_COLLAPSED + DIALOG_PADDING,
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const storeContentWidth = useCallback(
    (width: number) => {
      'worklet';

      contentWidthSV.value = withTiming(width);
    },
    [contentWidthSV],
  );

  const storeContentWidthDebounced = debounce(storeContentWidth, 400);

  const onContentLayout = useCallback(
    (event: LayoutChangeEvent) => {
      storeContentWidthDebounced(event.nativeEvent.layout.width);
    },
    [storeContentWidthDebounced],
  );

  const toggleAnimations = useCallback(() => {
    if (!isShown) return;

    const {
      ENTRY_SPRING_MASS,
      EXIT_DURATION,
      EXIT_DELAY,
      CONTENT_ENTRY_DURATION,
      CONTENT_ENTRY_DELAY,
      CONTENT_EXIT_DURATION,
    } = DIALOG_ANIMATIONS;

    isShownSV.value = withSpring(1, { mass: ENTRY_SPRING_MASS }); // prettier-ignore
    isContentShownSV.value = withDelay(CONTENT_ENTRY_DELAY, withTiming(1, { duration: CONTENT_ENTRY_DURATION })); // prettier-ignore

    return () => {
      isShownSV.value = withDelay(EXIT_DELAY, withTiming(0, { duration: EXIT_DURATION })); // prettier-ignore
      isContentShownSV.value = withTiming(0, { duration: CONTENT_EXIT_DURATION }); // prettier-ignore
    };
  }, [isContentShownSV, isShown, isShownSV]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const dialogAnimatedStyles = useAnimatedStyle(() => {
    const value = isShownSV.value;

    return {
      width: interpolate(value, [0, 1], [TOGGLE_SIZE, dialogWidthSV.value - DIALOG_PADDING]), // prettier-ignore
      height: interpolate(value, [0, 1], [DIALOG_SIZE_COLLAPSED, DIALOG_HEIGHT_EXPANDED]), // prettier-ignore
      paddingRight: interpolate(value, [0, 1], [0, DIALOG_PADDING]),
    };
  });

  const contentContainerAnimatedStyles = useAnimatedStyle(() => ({
    opacity: isContentShownSV.value,
  }));

  const dialogStyles = [dialogAnimatedStyles, styles.dialog];
  const contentContainerStyles = [
    contentContainerAnimatedStyles,
    isSmallScreen ? styles.contentContainerFluid : undefined,
    styles.contentContainer,
  ];

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(toggleAnimations, [toggleAnimations]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View
      pointerEvents="box-none"
      aria-expanded={isShown}
      style={isSmallScreen ? styles.containerSmallScreen : undefined}
    >
      <Animated.View style={dialogStyles}>
        <Toggle
          isDialogShown={isShown}
          dialogNativeId={dialogNativeId}
          onPress={onTogglePress}
          accessibilityLabel={toggleA11yLabel}
        />
      </Animated.View>

      <Animated.View
        accessible={isShown}
        accessibilityLabel={dialogA11yLabel}
        nativeID={dialogNativeId}
        onLayout={onContentLayout}
        style={contentContainerStyles}
        pointerEvents="none"
      >
        {children}
      </Animated.View>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerSmallScreen: {
    width: '100%',
    alignItems: 'flex-end',
  },
  dialog: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.colors.WHITE,
    borderRadius: theme.radius['3xLarge'],
    shadowColor: theme.colors.BLACK,
    shadowOffset: { width: 0, height: theme.spacing['1'] },
    shadowOpacity: 0.33,
    shadowRadius: theme.radius.xxlarge,
    elevation: 24,
  },
  contentContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    flexDirection: 'row',
    alignItems: 'center',
  },
  contentContainerFluid: {
    right: TOGGLE_SIZE,
    paddingRight: Platform.select({
      default: 0,
      native: TOGGLE_SIZE,
    }),
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  isShown: boolean;
  onTogglePress: () => void;
  dialogA11yLabel: string;
  toggleA11yLabel: string;
}>;
